import _ from 'lodash';
import React, {useCallback, useContext, useEffect, useState} from 'react';

const PreferencesContext = React.createContext({
    /** @type {(name: string, value: any) => void} */
    setGlobalPreference: () => null,
    /** @type {(name: string, value: any) => void} */
    setPreference: () => null,
    /** @type {Record<string, any>} */
    preferences: {},
    /** @type {Record<string, any>} */
    globalPreferences: {},
});

export function ScopedPreferences({name, children}) {
    const {setPreference, preferences, globalPreferences, setGlobalPreference} = useContext(PreferencesContext);

    const nestedSetPreference = useCallback(
        (key, val) => {
            setPreference(name + '.' + key, val);
        },
        [name, setPreference],
    );

    return (
        <PreferencesContext.Provider
            value={{
                preferences: _.get(preferences, name, {}),
                setPreference: nestedSetPreference,
                globalPreferences,
                setGlobalPreference,
            }}>
            {children}
        </PreferencesContext.Provider>
    );
}

export function WithPreferencesStorage(props) {
    const [preferenceState, setPreferenceState] = useState(null);

    // initialize the preferences by reading it from local storage
    useEffect(() => {
        // remove the old format of filters not to clutter local storage. TODO: To be removed in a month (18.11.23)
        localStorage.removeItem('UserPreferences');

        try {
            let preferences = localStorage.getItem('TarasaPreferences');

            if (_.isEmpty(preferences)) {
                setPreferenceState({});
            } else {
                preferences = JSON.parse(preferences);
                if (!_.isObject(preferences)) setPreferenceState({});
                else setPreferenceState(preferences);
            }
        } catch {
            // if anything goes wrong in initializing the filter context, just set it to empty
            setPreferenceState({});
        }
    }, []);

    const setPreference = useCallback(
        (contextName, value) => {
            setPreferenceState((ps) => {
                let newState = _.cloneDeep(ps);
                if (_.isFunction(value)) {
                    let oldValue = _.get(newState, contextName, null);
                    let newValue = value(oldValue);
                    _.set(newState, contextName, newValue);
                } else {
                    _.set(newState, contextName, value);
                }

                return newState;
            });
        },
        [setPreferenceState],
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const writePreferences = useCallback(
        // We use debounce here, because there are places where we update preferences in an onChange of a textbox, in
        // which case it does not make sense to store every value while the user is still typing
        _.debounce((p) => {
            if (p != null) {
                localStorage.setItem('TarasaPreferences', JSON.stringify(p, null, 4));
            }
        }, 1000),
        [],
    );

    useEffect(() => {
        writePreferences(preferenceState);
    }, [preferenceState, writePreferences]);

    // preferenceState is only null if we're still loading from localStorage
    if (preferenceState === null) return null;

    return (
        <PreferencesContext.Provider
            value={{
                preferences: preferenceState,
                globalPreferences: preferenceState,
                setPreference,
                setGlobalPreference: setPreference,
            }}>
            {props.children}
        </PreferencesContext.Provider>
    );
}

const usePreferences = () => useContext(PreferencesContext);
export {usePreferences};
